import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  Text, 
  TextInput, 
  Group, 
  Select,
  Tooltip, 
  Loader, 
  ScrollArea, 
  Image,
  Center,
  Alert,
  Divider,
  Button,
  Box,
  ActionIcon
} from '@mantine/core';
import { 
  IconSearch, 
  IconAlertCircle, 
  IconSword,
  IconShirt,
  IconBook,
  IconHorseToy,
  IconDiamond,
  IconMask,
  IconScissors,
  IconSwords,
  IconShield,
  IconArmchair,
  IconHelmet,
  IconSock,
  IconShoe,
  IconManualGearbox,
  IconWind,
  IconBriefcase,
  IconSkull,
  IconPaw,
  IconX
} from '@tabler/icons-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Item } from '../api/items';
import { useItems } from '../hooks/useItems';

// Create an icon cache at module level
const iconCache = new Map<string, string>();

// Helper function for debouncing
const debounce = <F extends (...args: any[]) => any>(
  func: F,
  waitFor: number
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: Parameters<F>) => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), waitFor);
  };

  return debounced as (...args: Parameters<F>) => ReturnType<F>;
};

interface ItemsListProps {
  onItemSelect: (item: Item) => void;
  selectedItem: Item | null;
}

/**
 * Items list component
 * Displays a list of items with search and filtering
 */
const ItemsList: React.FC<ItemsListProps> = ({ onItemSelect, selectedItem }) => {
  // State for search and filter
  const [search, setSearch] = useState('');
  const [itemType, setItemType] = useState<string>('');
  const [itemRank, setItemRank] = useState<string>('');
  const [itemGrade, setItemGrade] = useState<string>('');
  
  // Debounced search state
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // State for tooltip delayed loading
  const [hoveredItemId, setHoveredItemId] = useState<number | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Reference for virtual list
  const parentRef = React.useRef<HTMLDivElement>(null);
  
  // Update debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    
    return () => {
      clearTimeout(handler);
    };
  }, [search]);
  
  // Tooltip delay effect
  useEffect(() => {
    if (hoveredItemId === null) {
      setShowTooltip(false);
      return;
    }
    
    const timer = setTimeout(() => {
      setShowTooltip(true);
    }, 250);
    
    return () => {
      clearTimeout(timer);
      setShowTooltip(false);
    };
  }, [hoveredItemId]);
  
  // Fetch items with search and filters
  const { 
    items, 
    isLoading, 
    isError, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage,
    pagination
  } = useItems({
    search: debouncedSearch,
    type: itemType,
    rank: itemRank,
    grade: itemGrade
  });
  
  // Setup virtualizer for efficient rendering with optimized config
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60,
    overscan: 10, // Increased for smoother scrolling
    
    // Add dynamic measurement caching
    measureElement: (element) => {
      // Measure once and cache
      const height = element.getBoundingClientRect().height;
      return Math.max(60, height); // Ensure minimum height
    },
    
    // Recalculate layout less frequently during scrolling
    scrollToFn: (offset, options) => {
      const scrollingElement = parentRef.current;
      if (!scrollingElement) return;
      
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        scrollingElement.scrollTop = offset;
      });
    }
  });
  
  // Get the last visible item for infinite scrolling
  const lastVirtualItem = rowVirtualizer.getVirtualItems().at(-1);
  
  // Debounced scroll handler for infinite scrolling
  const scrollHandler = useCallback(
    debounce(() => {
      // Check if we need to load more data
      if (
        !hasNextPage || 
        isFetchingNextPage ||
        !lastVirtualItem ||
        lastVirtualItem.index < items.length - 1 - rowVirtualizer.options.overscan
      ) return;
      
      fetchNextPage();
    }, 150),
    [hasNextPage, isFetchingNextPage, lastVirtualItem, items.length, fetchNextPage, rowVirtualizer.options.overscan]
  );

  // Apply scroll listener
  useEffect(() => {
    const element = parentRef.current;
    if (!element) return;
    
    element.addEventListener('scroll', scrollHandler);
    return () => element.removeEventListener('scroll', scrollHandler);
  }, [scrollHandler]);
  
  // Get the icon URL for an item with caching
  const getItemIconUrl = useCallback((iconFileName: string) => {
    // Convert filename to lowercase to make it case-insensitive
    const normalizedFileName = iconFileName.toLowerCase();
    
    if (!iconCache.has(normalizedFileName)) {
      const url = `/icons/items/${normalizedFileName}.jpg`;
      iconCache.set(normalizedFileName, url);
      
      // Optionally preload the image
      if (typeof window !== 'undefined') {
        const img = new window.Image();
        img.src = url;
      }
    }
    return iconCache.get(normalizedFileName);
  }, []);
  
  // Determine item type icon (memoized)
  const getItemTypeIcon = useCallback((wearType: number, group: number, type: number) => {
    // Drop pet check (wear_type=501, group=18)
    if (wearType === 501 && group === 18) {
      return <IconPaw size={14} color="purple" stroke={1.5} />;
    }
    
    // Not wearable
    if (wearType === -1) {
      // Pet card (wear_type=-1, group=13, type=2)
      if (group === 13 && type === 2) {
        return <IconPaw size={14} color="orange" stroke={1.5} />;
      }
      return <IconBook size={14} color="gray" stroke={1.5} />;
    }
    
    // WEAPON: 1h weapon (0), 2h weapon (99), Shield (1)
    if ([0, 1, 99].includes(wearType)) {
      return <IconSword size={14} color="blue" stroke={1.5} />;
    }
    
    // ARMOR: Helmet (3), Glove (4), Boots (5), Belt (6), Cloak (7), Necklace (8), Earring (11)
    if ([3, 4, 5, 6, 7, 8, 11].includes(wearType)) {
      return <IconShirt size={14} color="green" stroke={1.5} />;
    }
    
    // Skill card (100)
    if (wearType === 100) {
      return <IconBook size={14} color="purple" stroke={1.5} />;
    }
    
    // Mount (22)
    if (wearType === 22) {
      return <IconHorseToy size={14} color="brown" stroke={1.5} />;
    }
    
    // Creature artifacts (200)
    if (wearType === 200) {
      return <IconDiamond size={14} color="indigo" stroke={1.5} />;
    }
    
    // Mask (12)
    if (wearType === 12) {
      return <IconMask size={14} color="teal" stroke={1.5} />;
    }
    
    // Hairstyle (13)
    if (wearType === 13) {
      return <IconScissors size={14} color="pink" stroke={1.5} />;
    }
    
    // Decorated items
    if (wearType === 14) return <IconSwords size={14} color="cyan" stroke={1.5} />;
    if (wearType === 15) return <IconShield size={14} color="lime" stroke={1.5} />;
    if (wearType === 16) return <IconArmchair size={14} color="violet" stroke={1.5} />;
    if (wearType === 17) return <IconHelmet size={14} color="grape" stroke={1.5} />;
    if (wearType === 18) return <IconSock size={14} color="red" stroke={1.5} />;
    if (wearType === 19) return <IconShoe size={14} color="yellow" stroke={1.5} />;
    if (wearType === 20) return <IconManualGearbox size={14} color="orange" stroke={1.5} />;
    
    // Wings (21)
    if (wearType === 21) {
      return <IconWind size={14} color="blue" stroke={1.5} />;
    }
    
    // Bag (23)
    if (wearType === 23) {
      return <IconBriefcase size={14} color="green" stroke={1.5} />;
    }
    
    // Boss card (198)
    if (wearType === 198) {
      return <IconSkull size={14} color="red" stroke={1.5} />;
    }
    
    // Default icon for other types
    return <IconBook size={14} color="gray" stroke={1.5} />;
  }, []);
  
  // Get rank display value (memoized)
  const getRankName = useCallback((rank: number): string => {
    const ranks: Record<number, string> = {
      0: 'R0',
      1: 'R1',
      2: 'R2',
      3: 'R3',
      4: 'R4',
      5: 'R5',   
      6: 'R6',
      7: 'R7'
    };
    
    return ranks[rank] || `Rank ${rank}`;
  }, []);
  
  // Get grade display value (memoized)
  const getGradeName = useCallback((grade: number): string => {
    const grades: Record<number, string> = {
      0: 'Неэфир',
      1: 'Нерушика',
      2: 'Демон',
      3: 'Ведьма'
    };
    
    return grades[grade] || `Grade ${grade}`;
  }, []);
  
  // Define type options for dropdown
  const typeOptions = useMemo(() => [
    { value: '', label: 'All Types', icon: null },
    { value: '-1', label: 'Not Wearable', icon: 'icon_item_potion_redpotion_lv3' },
    { value: '0', label: '1H Weapon', icon: 'icon_weapon_0041' },
    { value: '99', label: '2H Weapon', icon: 'icon_axe_tornadosoul' },
    { value: '1', label: 'Shield', icon: 'icon_shield_0003' },
    { value: '2', label: 'Armor', icon: 'icon_clothe_deva_warrior302' },
    { value: '3', label: 'Helmet', icon: 'icon_helmet_bandana' },
    { value: '4', label: 'Gloves', icon: 'icon_glove_0012' },
    { value: '5', label: 'Boots', icon: 'icon_boots_0001' },
    { value: '6', label: 'Belt', icon: 'icon_belt_great' },
    { value: '7', label: 'Cloak', icon: 'icon_mantle_skinny' },
    { value: '8', label: 'Necklace', icon: 'asf_necklace_redfever' },
    { value: '9', label: 'Rings', icon: 'icon_accessory_0011' },
    { value: '11', label: 'Earring', icon: 'icon_accessory_0046' },
    { value: '23', label: 'Bag', icon: 'icon_equip_bag_beginner' },
    { value: '12', label: 'Mask', icon: 'icon_mask_shadymask' },
    { value: '21', label: 'Wings', icon: 'icon_mantle_deco_luciferwing' },
    { value: '14', label: 'Decorated Weapon', icon: 'icon_bearload_twistarm_blackbear' },
    { value: '15', label: 'Decorated Shield', icon: 'icon_bearload_ironbear_blackbear' },
    { value: '16', label: 'Decorated Armor', icon: 'icon_bearload_body_soulful_blackbear' },
    { value: '17', label: 'Decorated Hat', icon: 'icon_bearload_helmet_soulful_blackbear' },
    { value: '18', label: 'Decorated Gloves', icon: 'icon_bearload_hand_soulful_blackbear' },
    { value: '19', label: 'Decorated Boots', icon: 'icon_bearload_foot_soulful_blackbear' },
    { value: '20', label: 'Decorated Cloak', icon: 'icon_mantle_ru_1001_6st' },
    { value: '198', label: 'Boss Card', icon: 'icon_card_boss' },
    { value: '100', label: 'Skill Card', icon: 'icon_card_devaskill' },
    { value: '22', label: 'Mount', icon: 'icon_ride_ornito_blue' },
    { value: '200', label: 'Creature Artifact', icon: 'icon_item_artifact_crirate' },
    { value: '13', label: 'Hairstyle', icon: 'icon_item_hair_a_003' },
    { value: '501', label: 'Drop Pet', icon: 'icon_pet_arcadia' },
  ], []);
  
  // Define rank options for dropdown
  const rankOptions = useMemo(() => [
    { value: '', label: 'All Ranks' },
    { value: '1', label: 'R1' },
    { value: '2', label: 'R2' },
    { value: '3', label: 'R3' },
    { value: '4', label: 'R4' },
    { value: '5', label: 'R5' },
    { value: '6', label: 'R6' },
    { value: '7', label: 'R7' }
  ], []);
  
  // Define grade options for dropdown
  const gradeOptions = useMemo(() => [
    { value: '', label: 'Все' },
    { value: '0', label: 'Неэфирные' },
    { value: '1', label: 'Нерушимые' },
    { value: '2', label: 'Демонические' },
    { value: '3', label: 'Ведьма' },
  ], []);
  
  // Render item with tooltip
  const renderItemRow = (item: Item, isSelected: boolean) => {
    return (
      <Tooltip
        label={<div dangerouslySetInnerHTML={{ __html: item.tooltip }} />}
        position="right"
        withArrow
        multiline
        w={220}
      >
        <div>
          <div 
            className={`p-2 flex items-center cursor-pointer transition-colors ${
              isSelected 
                ? 'bg-blue-100' 
                : 'hover:bg-gray-50'
            }`}
            onClick={() => onItemSelect(item)}
          >
            {/* Item icon */}
            <div className="w-10 h-10 mr-4 flex-shrink-0">
              <Image
                src={getItemIconUrl(item.icon_file_name)}
                alt={item.value}
                fallbackSrc="/icons/fallback.jpg"
                fit="contain"
                className="rounded"
              />
            </div>
            
            {/* Item name with type icon */}
            <div className="flex-grow flex flex-col">
              <div className="flex items-center">
                <Text size="sm" fw={isSelected ? 700 : 500} lineClamp={1} style={{ marginLeft: '8px' }}>
                  {item.value}
                </Text>
                <div className="mr-2 flex-shrink-0" style={{ marginLeft: '4px' }}>
                  {getItemTypeIcon(item.wear_type, item.group, item.type)}
                </div>
              </div>
              
              {/* Item details */}
              <div className="flex items-center ml-2">
                <Text size="xs" c="dimmed">
                  {getRankName(item.rank)} | {getGradeName(item.grade)}
                  {item.socket > 0 ? ` | Sockets: ${item.socket}` : ''}
                </Text>
              </div>
            </div>
          </div>
          <Divider my={0} opacity={0.2} />
        </div>
      </Tooltip>
    );
  };
  
  return (
    <div className="h-full flex flex-col px-2">
      
      {/* Search input */}
      <TextInput
        placeholder="Search items..."
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        rightSection={
          search ? (
            <ActionIcon onClick={() => setSearch('')} size="sm" variant="subtle">
              <IconX size={16} />
            </ActionIcon>
          ) : null
        }
        style={{ flex: 0 }}
        size="sm"
        mb="xs"
      />
      
      {/* Filter selects */}
      <Group mb="md" grow>
        <Select
          placeholder="Type"
          value={itemType}
          onChange={(value) => setItemType(value || '')}
          data={typeOptions}
          size="xs"
          clearable
          renderOption={({ option }) => {
            const optionWithIcon = option as { value: string; label: string; icon: string | null };
            return (
              <Group gap="xs" wrap="nowrap">
                {optionWithIcon.icon && (
                  <Image
                    src={`/icons/items/${optionWithIcon.icon.toLowerCase()}.jpg`}
                    width={18}
                    height={18}
                    fit="contain"
                    fallbackSrc="/icons/fallback.jpg"
                    alt={String(optionWithIcon.label)}
                    className="rounded"
                  />
                )}
                <Text size="xs">{optionWithIcon.label}</Text>
              </Group>
            );
          }}
        />
        
        <Select
          placeholder="Rank"
          value={itemRank}
          onChange={(value) => setItemRank(value || '')}
          data={rankOptions}
          size="xs"
          clearable
        />
        
        <Select
          placeholder="Grade"
          value={itemGrade}
          onChange={(value) => setItemGrade(value || '')}
          data={gradeOptions}
          size="xs"
          clearable
        />
      </Group>
      
      {/* Items list */}
      <div className="flex-grow overflow-hidden rounded-md">
        <ScrollArea className="h-full" viewportRef={parentRef}>
          {isLoading && !items.length ? (
            <Center style={{ height: '100%' }}>
              <Loader size="md" />
            </Center>
          ) : isError ? (
            <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
              Failed to load items
            </Alert>
          ) : items.length === 0 ? (
            <Center style={{ height: '100%' }}>
              <Text c="dimmed">No items found</Text>
            </Center>
          ) : (
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                const item = items[virtualItem.index];
                const isSelected = selectedItem?.id === item.id;
                
                return (
                  <div
                    key={virtualItem.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    {renderItemRow(item, isSelected)}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>
      
      {/* Load more button */}
      {hasNextPage && (
        <Box mt="sm" className="flex justify-center">
          <Button
            size="xs"
            variant="light"
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
            disabled={isFetchingNextPage}
          >
            Load more ({pagination.loadedItems} of {pagination.totalItems})
          </Button>
        </Box>
      )}
    </div>
  );
};

export default ItemsList; 